import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { AbstractControl, FormControl, NonNullableFormBuilder, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  iconoirMail,
  iconoirLock,
  iconoirArrowRight,
  iconoirCheckCircle,
  iconoirWarningCircle
} from '@ng-icons/iconoir';
import { SupabaseService } from '../../../core/supabase.service';
import { NotificationsService } from '../../../services/notifications.service';
import { Subscription } from '@supabase/supabase-js';
import { BtnComponent } from '../../../components/ui/buttons/btn/btn.component';
import { ThemeToggleComponent } from '../../../components/ui/buttons/theme-toggle/theme-toggle.component';
import { authMascot } from '../auth-mascots';
import { PASSWORD_PATTERN } from '../password-rules';

/** Validateur : les deux champs password doivent être identiques */
const passwordsMatchValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const newPassword = control.get('newPassword')?.value;
  const confirmPassword = control.get('confirmPassword')?.value;
  return newPassword && confirmPassword && newPassword !== confirmPassword
    ? { passwordsMismatch: true }
    : null;
};

type ResetView = 'request' | 'recovery' | 'success-request' | 'success-recovery';

@Component({
  selector: 'app-reset-password',
  imports: [
    ReactiveFormsModule,
    RouterLink,
    NgIconComponent,
    BtnComponent,
    ThemeToggleComponent
  ],
  providers: [
    provideIcons({
      iconoirMail,
      iconoirLock,
      iconoirArrowRight,
      iconoirCheckCircle,
      iconoirWarningCircle
    })
  ],
  templateUrl: './reset-password.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './reset-password.component.scss'
})
export class ResetPasswordComponent implements OnInit, OnDestroy {

  private readonly supabase = inject(SupabaseService);
  private readonly notifications = inject(NotificationsService);
  private readonly router = inject(Router);
  private readonly fb = inject(NonNullableFormBuilder);

  public readonly view = signal<ResetView>('request');
  public readonly pending = signal(false);
  public readonly errorMessage = signal<string | null>(null);

  public readonly passwordPattern = PASSWORD_PATTERN;

  public readonly requestForm = this.fb.group({
    email: new FormControl('', [Validators.required, Validators.email])
  });

  public readonly recoveryForm = this.fb.group(
    {
      newPassword: new FormControl('', [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(this.passwordPattern)
      ]),
      confirmPassword: new FormControl('', [Validators.required])
    },
    { validators: passwordsMatchValidator }
  );

  public readonly showPasswordRules = signal(false);

  /** Poses de Koffi par étape (catalogue MOKKIE_POSES). */
  public readonly mascots = {
    reflexion: authMascot('reflexion'),
    lecture: authMascot('lecture'),
    guide: authMascot('guide'),
    bravo: authMascot('bravo')
  };

  private authSubscription?: ReturnType<typeof this.supabase.client.auth.onAuthStateChange>['data']['subscription'];

  ngOnInit(): void {
    // Détecter si on revient depuis le lien de reset (session PASSWORD_RECOVERY)
    const { data: { subscription } } = this.supabase.client.auth.onAuthStateChange((event, _session) => {
      if (event === 'PASSWORD_RECOVERY') {
        this.view.set('recovery');
      }
    });
    this.authSubscription = subscription;

    // Vérifier la session courante : si on est déjà en PASSWORD_RECOVERY au chargement
    this.supabase.client.auth.getSession().then(({ data: { session } }) => {
      // supabase-js positionne amr sur 'recovery' dans ce cas
      if (session?.user?.aud === 'authenticated' && this.view() === 'request') {
        // On ne fait rien : onAuthStateChange a déjà déclenché si PASSWORD_RECOVERY
      }
    });
  }

  ngOnDestroy(): void {
    this.authSubscription?.unsubscribe();
  }

  public async requestReset(): Promise<void> {
    this.requestForm.markAllAsTouched();
    if (!this.requestForm.valid) return;

    this.pending.set(true);
    this.errorMessage.set(null);

    const email = this.requestForm.getRawValue().email ?? '';
    const origin = window.location.origin;

    const { error } = await this.supabase.client.auth.resetPasswordForEmail(email, {
      redirectTo: `${origin}/reset-password`
    });

    this.pending.set(false);

    if (error) {
      this.errorMessage.set(
        error.status === 429
          ? 'Un lien vient déjà de vous être envoyé. Patientez une minute avant d\'en demander un nouveau.'
          : `Le lien n'a pas pu être envoyé (${error.message}). Vérifiez l'adresse, puis réessayez.`
      );
      return;
    }

    this.view.set('success-request');
  }

  public async updatePassword(): Promise<void> {
    this.recoveryForm.markAllAsTouched();
    if (!this.recoveryForm.valid) return;

    this.pending.set(true);
    this.errorMessage.set(null);

    const { newPassword } = this.recoveryForm.getRawValue();
    if (!newPassword) return;

    const { error } = await this.supabase.client.auth.updateUser({ password: newPassword });

    this.pending.set(false);

    if (error) {
      this.errorMessage.set(
        `Votre mot de passe n'a pas pu être enregistré (${error.message}). Réessayez, ou demandez un nouveau lien si celui-ci a expiré.`
      );
      return;
    }

    this.view.set('success-recovery');
    this.notifications.showSuccess('Mot de passe mis à jour');

    // Rediriger après 2s
    setTimeout(() => {
      this.router.navigate(['/'], { replaceUrl: true });
    }, 2000);
  }
}
